import { Card, CardContent, CardFooter } from '@components/common/card';
import axios from 'axios';
import { FileItems, FileType } from '@pma/types';
import PmsLoader from '@components/common/loader';
import { Button } from '@components/common/button';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SERVER_URL } from '@pma/utils/api-routes';
import { parseCookies } from 'nookies';
import { ACCESS_TOKEN } from '@pma/utils/constants';
import { useAuth } from '@stores/auth-store';
import useTranslator from '@hooks/use-translator';
import { FilePreview } from '../dashboard/preview-file';
import DownloadFile from './download-file';
import DeleteFile from '../dashboard/delete-file';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@components/common/hover-card';
import { RenderFileIcon } from './files-view';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PrintFile from './print-file';
import ShareFileOnWhatsApp from './share-file-on-whatsapp';
import ShareFileOnEmail from './share-file-on-email';

const AllUserFiles = () => {
  const { translate } = useTranslator();

  const { authConfig } = useAuth();
  const orgId = authConfig?.last_organization_id;

  const navigate = useNavigate();
  const [searchParam] = useSearchParams();

  const queryKey = searchParam.get('revalidate');

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const getFiles = async ({ pageParam = 1 }) => {
    const response = await axios.get(
      SERVER_URL +
        `/files/getAll/byCurrentUser?deleted=false&paging_start_page=${pageParam}&paging_per_page=${25}&organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['all-files'],
    queryFn: ({ pageParam = 1 }) => getFiles({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined;
      }
      return allPages.length + 1;
    },
  });

  const handleLoadMoreData = async () => {
    await fetchNextPage();
  };

  const isEmpty = !data?.pages[0]?.itemsReceived;

  useEffect(() => {
    if (queryKey === 'projects') {
      refetch();
      navigate(`${location.pathname}`);
    }
  }, [queryKey]);

  return (
    <Card className='flex flex-col gap-2 items-center'>
      <CardContent className='overflow-y-auto overflow-x-hidden grid grid-cols-3 md:grid-cols-6 gap-6 w-full p-2 '>
        {data &&
          data.pages?.map((page) =>
            page?.items?.map((file: FileItems) => (
              <div
                className='flex items-center justify-center h-[170px] p-2 '
                key={file?.id}
              >
                <HoverCard>
                  <FilePreview file={file}>
                    <HoverCardTrigger className='w-full flex flex-col gap-4 items-center'>
                      <RenderFileIcon size='lg' type={file?.type as FileType} />
                      <span className='truncate w-[10ch] text-ellipsis text-xs text-center'>
                        {file?.name}
                      </span>
                    </HoverCardTrigger>
                  </FilePreview>

                  <HoverCardContent className='flex items-center w-fit gap-2'>
                    <DownloadFile file={file} />
                    <PrintFile file={file} />
                    <ShareFileOnWhatsApp file={file} />
                    <ShareFileOnEmail file={file} />
                    <DeleteFile file={file} />
                  </HoverCardContent>
                </HoverCard>
              </div>
            )),
          )}

        {isEmpty && !isFetching && (
          <small className='w-full col-span-4 font-semibold text-lg h-full flex items-center justify-center'>
            No Files found
          </small>
        )}
        {isFetchingNextPage && (
          <div style={{ width: '100%' }}>
            <PmsLoader />
          </div>
        )}
      </CardContent>
      <CardFooter className='p-6'>
        {hasNextPage ? (
          <Button
            variant={'link'}
            className='text-primary'
            onClick={handleLoadMoreData}
          >
            {translate('Load More')}
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  );
};

export default AllUserFiles;
